import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { deleteExecutionRun } from '@/features/executionRuns/data/executionRuns';
import { useUpdateExecutionRunTags } from '@/features/executionRuns/hooks/useExecutionRunMutations';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useNotifications } from '@/providers/notificationHooks';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import {
  invalidateBuilds,
  gridPaths,
  invalidateExecutionRuns,
  invalidateExecutionRunSummaries,
  invalidateGridQuery,
  invalidateMetrics,
  invalidateSuites,
  queryKeys,
  invalidateSuiteRuns,
  invalidateTests,
  invalidateTestRuns,
  useInvalidateQueries,
} from '@/shared/hooks';
import type { ExecutionRunDto } from '@flowqa/shared';

type DeleteTarget = {
  id: string;
  name: string;
};

type UseExecutionRunsTableRowActionsParams = {
  selectedProjectId: string | null;
};

type UseExecutionRunsTableRowActionsResult = {
  deleteLoading: boolean;
  deleteRowPendingId: string | null;
  deleteTarget: DeleteTarget | null;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onDeleteRow: (row: ExecutionRunDto) => void;
  onUpdateRowTagsColor: (input: {
    row: ExecutionRunDto;
    tags: string[];
    colors: TagColor[];
  }) => void;
  updateRowTagsColorPendingId: string | null;
};

export const useExecutionRunsTableRowActions = ({
  selectedProjectId,
}: UseExecutionRunsTableRowActionsParams): UseExecutionRunsTableRowActionsResult => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const updateExecutionRunTagsMutation = useUpdateExecutionRunTags();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [updatingTagRowId, setUpdatingTagRowId] = useState<string | null>(null);

  const deleteExecutionRunMutation = useMutation({
    mutationFn: async (input: { id: string }) => {
      await deleteExecutionRun(input, selectedOrganisationId);
      return input;
    },
    onSuccess: async (_deleted, input) => {
      await invalidateQueries([
        invalidateExecutionRuns(selectedOrganisationId ?? null, selectedProjectId ?? null),
        invalidateExecutionRunSummaries(),
        invalidateSuiteRuns(selectedProjectId ?? null, selectedOrganisationId ?? null),
        invalidateTestRuns(selectedProjectId ?? null, selectedOrganisationId ?? null),
        invalidateSuites(selectedProjectId ?? null, selectedOrganisationId ?? null),
        invalidateTests(selectedProjectId ?? null, selectedOrganisationId ?? null),
        invalidateBuilds(selectedProjectId ?? null, selectedOrganisationId ?? null),
        invalidateMetrics(selectedOrganisationId ?? null, selectedProjectId ?? null),
        invalidateGridQuery(gridPaths.executionRuns),
        invalidateGridQuery(gridPaths.suiteRuns),
        invalidateGridQuery(gridPaths.testRuns),
        invalidateGridQuery(gridPaths.suites),
        invalidateGridQuery(gridPaths.tests),
        invalidateGridQuery(gridPaths.builds),
      ]);
      notify({
        message: getToastMessage('entity.deleted', {
          entity: 'execution run',
          name: deleteTarget?.name ?? input.id,
        }),
        tone: 'green',
      });
      setDeleteTarget(null);
    },
    onError: (error) => {
      notify({ message: toErrorMessage(error), tone: 'red' });
    },
  });

  const updateTagColorMutation = useMutation({
    mutationFn: async (input: { tags: string[]; colors: TagColor[] }) => {
      const updates = input.tags
        .map((name, index) => ({ name: name.trim(), color: input.colors[index] ?? 'neutral' }))
        .filter((item) => item.name.length > 0);
      if (updates.length === 0) return;
      await Promise.all(
        updates.map(async (item) => {
          await updateTagColor(item, selectedOrganisationId ?? undefined);
        }),
      );
    },
  });

  const onDeleteRow = useCallback((row: ExecutionRunDto) => {
    setDeleteTarget({ id: row.id, name: row.name?.trim() ?? row.id });
  }, []);

  const onDeleteClose = useCallback(() => {
    if (deleteExecutionRunMutation.isPending) return;
    setDeleteTarget(null);
  }, [deleteExecutionRunMutation.isPending]);

  const onDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    deleteExecutionRunMutation.mutate({ id: deleteTarget.id });
  }, [deleteExecutionRunMutation, deleteTarget]);

  const onUpdateRowTagsColor = useCallback(
    ({ row, tags, colors }: { row: ExecutionRunDto; tags: string[]; colors: TagColor[] }) => {
      const normalizedTags = Array.from(
        new Set(tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0)),
      );
      const normalizedColors = colors.slice(0, normalizedTags.length);
      setUpdatingTagRowId(row.id);
      void (async () => {
        try {
          await updateExecutionRunTagsMutation.mutateAsync({ id: row.id, tags: normalizedTags });
          if (normalizedTags.length > 0) {
            await updateTagColorMutation.mutateAsync({
              tags: normalizedTags,
              colors: normalizedColors,
            });
          }
          notify({ message: getToastMessage('tags.updated'), tone: 'green' });
          await queryClient.invalidateQueries({
            queryKey: queryKeys.tagOptions(selectedProjectId, selectedOrganisationId ?? null),
          });
          await invalidateQueries([
            invalidateExecutionRuns(selectedOrganisationId ?? null, selectedProjectId ?? null),
            invalidateGridQuery(gridPaths.executionRuns),
            invalidateGridQuery(gridPaths.suiteRuns),
            invalidateGridQuery(gridPaths.testRuns),
          ]);
        } catch (error) {
          notify({ message: toErrorMessage(error), tone: 'red' });
        } finally {
          setUpdatingTagRowId(null);
        }
      })();
    },
    [
      invalidateQueries,
      notify,
      queryClient,
      selectedOrganisationId,
      selectedProjectId,
      toErrorMessage,
      updateExecutionRunTagsMutation,
      updateTagColorMutation,
    ],
  );

  return {
    deleteLoading: deleteExecutionRunMutation.isPending,
    deleteRowPendingId: deleteExecutionRunMutation.isPending ? (deleteTarget?.id ?? null) : null,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId:
      updateTagColorMutation.isPending || updateExecutionRunTagsMutation.isPending
        ? updatingTagRowId
        : null,
  };
};
